import { Scenario, type Scenario as ScenarioType } from "@radar-sim/schema";

export { migrateAndParse, ScenarioMigrationError } from "./migrate.js";
export type { MigrationResult } from "./migrate.js";

/**
 * Parse a map of (path → raw JSON) into a validated scenario map keyed by
 * metadata.id. Throws on first validation failure with a diagnostic that
 * includes the failing file path. Pure / testable.
 */
export function buildBundledMap(
  modules: Record<string, unknown>,
): Map<string, ScenarioType> {
  const bundled = new Map<string, ScenarioType>();
  for (const [path, raw] of Object.entries(modules)) {
    const parsed = Scenario.safeParse(raw);
    if (!parsed.success) {
      throw new Error(
        `bundled scenario ${path} failed validation: ${parsed.error.message}`,
      );
    }
    bundled.set(parsed.data.metadata.id, parsed.data);
  }
  return bundled;
}

/**
 * Bundled scenarios. Imports via `import.meta.glob` are picked up by Vite
 * (and Vitest, which is Vite-based) and resolved into a static module map
 * at build time. The call MUST be in canonical form — Vite's transform
 * only matches `import.meta.glob(...)` as a literal call expression; any
 * surrounding type assertion or optional chain defeats the transform and
 * leaves the map empty in production.
 *
 * Each scenario is parsed against the schema at module load. CI also
 * validates the raw JSON via `pnpm validate-scenarios`.
 */
const modules: Record<string, unknown> = import.meta.glob(
  "../data/*.json",
  { eager: true, import: "default" },
);

const bundled = buildBundledMap(modules);

export function getBundledScenario(id: string): ScenarioType | undefined {
  return bundled.get(id);
}

export function listBundledScenarios(): readonly ScenarioType[] {
  return [...bundled.values()];
}
